import useSession from "../hooks/use-session";
import { AreaScope } from "@/components/common/has-access";
import ThreeDotsLoader from "@/components/common/three-dots-loader";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { Copy, File } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getInitials } from "@/helpers/get-initials";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";
import { BundleUsageTable } from "@/features/users/ui/bundle-usage-table";
import { useUpdateUtilizedSessions } from "@/features/users/hooks/use-update-utilized-sessions";
import { useCancelSession } from "../hooks/use-cancel-session";
import { useCreateDocument } from "../hooks/use-create-document";
import { useCreateNoShow } from "../hooks/use-create-no-show";
import { useUndoNoShow } from "../hooks/use-undo-no-show";
import { useUpdateBundleUsageStatus } from "@/features/users/hooks/use-update-bundle-usage-status";
import { Session } from "@/pages/sessions/types/session";
import { isWomensHealthBundle } from "@/helpers/womens-health-schedule";

const DASHBOARD_BASE_URL =
  (import.meta.env.VITE_DASHBOARD_URL as string) ??
  "https://dashboard.rockethealth.app";

interface SessionDetailsDrawerProps {
  sessionId: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SessionDetailsDrawer({
  sessionId,
  isOpen,
  onOpenChange,
}: SessionDetailsDrawerProps) {
  const { data: session, isLoading, error, isFetching } = useSession(
    sessionId || "",
    isOpen && !!sessionId
  );
  const { mutate: updateUtilized } = useUpdateUtilizedSessions();
  const { mutate: cancelSession } = useCancelSession();
  const { mutate: createDocument } = useCreateDocument();
  const { mutate: createNoShow } = useCreateNoShow();
  const { mutate: undoNoShow } = useUndoNoShow();
  const { mutate: updateBundleStatus } = useUpdateBundleUsageStatus();

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", description: `${label} copied to clipboard` });
    } catch {
      toast({
        title: "Error",
        description: `Failed to copy ${label}`,
        variant: "destructive",
      });
    }
  };

  const formatSessionDate = (date: string) => {
    return format(new Date(date), "EEEE, MMMM d, yyyy");
  };

  const formatSessionTime = (start: string, end: string) => {
    return `${format(new Date(start), "h:mm a")} - ${format(new Date(end), "h:mm a")}`;
  };

  const handleCopyMeetingLink = () => {
    if (session?.location?.join_url) {
      copyToClipboard(session.location.join_url, "Meeting Link");
    }
  };

  const handleJoinMeeting = () => {
    if (session?.location?.join_url) {
      window.open(session.location.join_url, "_blank");
    }
  };

  // Judged as sessions wherever this drawer is opened from — see AreaScope.
  return (
    <AreaScope area="sessions">
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0" style={{ width: '100%', maxWidth: '56rem' }}>
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-2xl font-bold text-gray-900">Session Details</DialogTitle>
        </DialogHeader>
        <div className="max-h-[calc(90vh-8rem)] overflow-y-auto overflow-x-hidden">
          <div className="w-full py-4 space-y-4 px-6 min-w-0 overflow-x-hidden max-w-full" style={{ display: 'block' }}>

            {error ? (
              <div className="text-center py-8">
                <div className="text-red-600 font-semibold">Failed to load session details</div>
                {error instanceof Error && error.message && (
                  <div className="text-sm text-red-500 mt-2">{error.message}</div>
                )}
              </div>
            ) : isFetching || isLoading || !session ? (
              <div className="flex justify-center items-center w-full min-h-[400px] py-8">
                <ThreeDotsLoader />
              </div>
            ) : (
              <>
                
                <div className="mb-4 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 truncate">{session.name}</h3>
                </div>

                
                {(() => {
                  // Prefer the session's own attendee. `usage_bundle.users[0]`
                  // is an unordered M2M, so on a couples pack it can surface the
                  // OTHER partner's age / sex / country / mental-health doc.
                  const user =
                    session?.participants?.[0]?.user ??
                    (session.usage_bundle as any)?.users?.[0];
                  const userAge = user?.age || null;
                  const userSex = user?.sex || null;
                  const userCountry = user?.country || null;
                  const userLanguages = Array.isArray(user?.language_preference) 
                    ? user.language_preference.join(", ")
                    : user?.language_preference || null;
                  const mentalHealthDocId = user?.document_ids?.["Mental health"] 
                    ? String(user.document_ids["Mental health"]).trim() 
                    : null;

                  return (
                    <>
                      {/* Therapist Details */}
                      {session.professional && (
                        <Card className="bg-white border border-orange-500 rounded-lg overflow-hidden">
                          <CardContent className="p-4 min-w-0">
                            <h3 className="text-base font-bold text-gray-900 mb-3">Therapist Details</h3>
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="w-12 h-12 rounded-full bg-gray-400 flex items-center justify-center text-white font-semibold flex-shrink-0">
                                {getInitials(session.professional.name)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-bold text-gray-900 truncate">{session.professional.name}</div>
                                <div className="text-sm text-gray-500 truncate">{session.professional.department || "Therapy"}</div>
                              </div>
                              <div className="text-right text-sm text-gray-700 flex-shrink-0">
                                <div className="whitespace-nowrap">Day: {formatSessionDate(session.start_time)}</div>
                                <div className="whitespace-nowrap">Time: {formatSessionTime(session.start_time, session.end_time)}</div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Patient Details */}
                      <Card className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <CardContent className="p-4">
                          <h3 className="text-base font-bold text-gray-900 mb-3">Patient Details</h3>
                          <div className="grid grid-cols-2 gap-4 min-w-0">
                            {/* Left Column */}
                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-gray-700">User: </span>
                                <span className="text-green-600 font-medium">{session.invitee_full_name || "—"}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-gray-700">Phone Number: </span>
                                <span className="text-gray-900">{session.invitee_phone || "—"}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-gray-700">Email: </span>
                                <span className="text-gray-900">{session.invitee_email || "—"}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-gray-700">Sex: </span>
                                <span className="text-gray-900">{userSex || "—"}</span>
                              </div>
                            </div>

                            
                            <div className="space-y-2">
                              <div className="text-sm">
                                <span className="font-medium text-gray-700">Age: </span>
                                <span className="text-gray-900">{userAge || "—"}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-gray-700">Country: </span>
                                <span className="text-gray-900">{userCountry || "—"}</span>
                              </div>
                              <div className="text-sm">
                                <span className="font-medium text-gray-700">Languages: </span>
                                <span className="text-gray-900">{userLanguages || "—"}</span>
                              </div>
                            </div>
                          </div>
                          {mentalHealthDocId && mentalHealthDocId.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex items-center gap-2">
                                <File className="h-4 w-4 text-gray-600" />
                                <a
                                  href={`https://docs.google.com/document/d/${mentalHealthDocId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                >
                                  Mental Health Document
                                </a>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 flex-shrink-0"
                                  onClick={() => {
                                    const docUrl = `https://docs.google.com/document/d/${mentalHealthDocId}`;
                                    copyToClipboard(docUrl, "Mental Health Document");
                                  }}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  );
                })()}

                {/* Bundle Details */}
                {session.usage_bundle && (() => {
                  const bundle = session.usage_bundle;
                  // Same ordering hazard as above; session.user_id is now
                  // resolved server-side from the attendance row, so it wins.
                  const user =
                    session?.participants?.[0]?.user ??
                    (bundle as any)?.users?.[0];
                  const userId = session.user_id || user?.id;
                  
                  if (!userId) {
                    return null;
                  }

                  return (
                    <div className="w-full min-w-0 overflow-x-auto">
                      <BundleUsageTable
                        bundles={[bundle]}
                        userId={userId}
                        onCreatePackClick={() => {}}
                      onUpdateUtilized={(params: any) => {
                        updateUtilized(params, {
                          onSuccess: () => {
                            toast({
                              title: "Utilized sessions updated successfully",
                            });
                          },
                          onError: () => {
                            toast({
                              title: "Failed to update utilized sessions",
                              variant: "destructive",
                            });
                          },
                        });
                      }}
                      onCancelSession={(sessionId: string) => {
                        const foundSession = bundle.sessions?.find((s: Session) => s.id === sessionId);
                        if (foundSession?.source_id) {
                          const shouldDecrementUsage = foundSession.status === "active" && !foundSession.is_no_show;
                          
                          cancelSession(
                            {
                              source_id: foundSession.source_id,
                              reason: undefined,
                            },
                            {
                              onSuccess: () => {
                                if (shouldDecrementUsage) {
                                  const currentUtilized = bundle.utilized_session_quantity;
                                  const newUtilized = Math.max(0, currentUtilized - 1);
                                  const wasCompleted = bundle.status === "COMPLETED";
                                  const endDate = new Date(bundle.end_date);
                                  const isExpired = endDate < new Date();
                                  
                                  updateUtilized(
                                    {
                                      usage_id: bundle.id,
                                      new_utilization_session__count: newUtilized,
                                    },
                                    {
                                      onSuccess: () => {
                                        if (wasCompleted && newUtilized < bundle.total_session_quantity) {
                                          let newStatus = "ACTIVE";
                                          if (isExpired) {
                                            newStatus = "EXPIRED";
                                          }
                                          
                                          updateBundleStatus(
                                            {
                                              bundle_id: bundle.id,
                                              status: newStatus,
                                            },
                                            {
                                              onSuccess: () => {
                                                toast({
                                                  title: "Success",
                                                  description: "Session cancelled, bundle usage and status updated",
                                                });
                                              },
                                              onError: () => {
                                                toast({
                                                  title: "Success",
                                                  description: "Session cancelled and bundle usage updated",
                                                });
                                              },
                                            }
                                          );
                                        } else {
                                          toast({
                                            title: "Success",
                                            description: "Session cancelled and bundle usage updated",
                                          });
                                        }
                                      },
                                      onError: () => {
                                        toast({
                                          title: "Session cancelled",
                                          description: "Bundle usage will update shortly",
                                        });
                                      },
                                    }
                                  );
                                }
                              },
                            }
                          );
                        }
                      }}
                      onCreateDoc={(sessionId: string) => {
                        createDocument(sessionId);
                      }}
                      onMarkNoShow={(sessionId: string) => {
                        const foundSession = bundle.sessions?.find((s: Session) => s.id === sessionId);
                        if (foundSession?.source_id) {
                          if (foundSession.is_no_show) {
                            undoNoShow({ source_id: foundSession.source_id });
                          } else {
                            createNoShow({ source_id: foundSession.source_id });
                          }
                        }
                      }}
                      onRescheduleSession={async (sessionId: string) => {
                        const foundSession = bundle.sessions?.find((s: Session) => s.id === sessionId);
                        if (foundSession?.source_id) {
                          try {
                            const lowerName = (foundSession.name || "").toLowerCase();
                            let sessionType: "individual" | "joint" | undefined;

                            if (lowerName.includes("joint")) {
                              sessionType = "joint";
                            } else if (
                              lowerName.includes("individual") ||
                              lowerName.includes("partner")
                            ) {
                              sessionType = "individual";
                            } else if (lowerName.includes("couple")) {
                              sessionType = "joint";
                            }

                            const sessionTypeQuery = sessionType ? `?session_type=${sessionType}` : "";
                            const isWomens = isWomensHealthBundle(bundle.bundle?.standard_id ?? "");
                            const baseUrl = isWomens ? DASHBOARD_BASE_URL : "https://rockethealth.app";
                            const rescheduleLink = `${baseUrl}/reschedule/${foundSession.source_id}${sessionTypeQuery}`;
                            navigator.clipboard.writeText(rescheduleLink);
                            toast({
                              title: "Copied",
                              description: "Reschedule link copied to clipboard",
                            });
                          } catch {
                            toast({
                              title: "Error",
                              description: "Failed to copy reschedule link",
                              variant: "destructive",
                            });
                          }
                        } else {
                          toast({
                            title: "Error",
                            description: "Session source_id not available",
                            variant: "destructive",
                          });
                        }
                      }}
                        />
                    </div>
                  );
                })()}

                
                <div className="flex gap-3 pt-4">
                  {session.status === "active" && session.location?.join_url && (
                    <Button
                      onClick={handleJoinMeeting}
                      className="bg-orange-500 text-white hover:bg-orange-600 flex-1"
                    >
                      Join Meeting
                    </Button>
                  )}
                  {session.location?.join_url && (
                    <Button
                      onClick={handleCopyMeetingLink}
                      variant="outline"
                      className="border-orange-500 text-orange-500 hover:bg-orange-50 flex-1"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Meeting Link
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </AreaScope>
  );
}
